import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import Modal from 'react-bootstrap/Modal'
import I18n from '../../../resources/i18n/I18n'
import PatrimonyApi from '../../../services/patrimonyApi'
import UserApi from '../../../services/userApi'
import { CONSTANTS } from '../../../utils/constants'
import { errorToast, successToast } from '../../../utils/toast'
import '../../buttons/customButton.scss'
import WrapperLoader from '../../loader/WrapperLoader'
import EstateTreeView from './EstateTreeView'
import './ModalEditAddress.scss'
import TransferButtons from './TransferButtons'
const getUserName = (user) => {
  if (!user) return ''
  return user.fullName || user.name || `${user.firstname} ${user.lastname}`.trim()
}

const ModalEditAddress = ({ show, onHide, user }) => {
  const userName = getUserName(user)
  const [loading, setLoading] = useState(false)
  const [selectedAvailable, setSelectedAvailable] = useState([])
  const [selectedAssociated, setSelectedAssociated] = useState([])
  const [searchAvailable, setSearchAvailable] = useState('')
  const [searchAssociated, setSearchAssociated] = useState('')
  const [patrimonies, setPatrimonies] = useState([])
  const [initialAssociatedIds, setInitialAssociatedIds] = useState([])
  const [associatedAgencies, setAssociatedAgencies] = useState([])
  const [expandedAvailableItems, setExpandedAvailableItems] = useState([])
  const [expandedAssociatedItems, setExpandedAssociatedItems] = useState([])

  const hasChanges = useMemo(() => {
    const currentAssociatedIds = associatedAgencies.sort().join(',')
    const initialIds = initialAssociatedIds.sort().join(',')
    return currentAssociatedIds !== initialIds
  }, [associatedAgencies, initialAssociatedIds])

  const fetchPatrimonies = useCallback(() => {
    setLoading(true)
    PatrimonyApi.getPatrimonyList()
      .then((response) => {
        setPatrimonies(response.records || [])
        const initialIds = user && user.agencies ? user.agencies.map((agency) => agency.id) : []
        setInitialAssociatedIds(initialIds)
        setAssociatedAgencies(initialIds)
        setSelectedAssociated([])
      })
      .catch(() => {
        errorToast(I18n.t('ErrorLoadingPatrimonies'))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [user])

  useEffect(() => {
    if (show) {
      fetchPatrimonies()
    }
  }, [show, fetchPatrimonies])

  const handleClose = () => {
    setSelectedAvailable([])
    setSelectedAssociated(initialAssociatedIds)
    setSearchAvailable('')
    setSearchAssociated('')
    setExpandedAvailableItems([])
    setExpandedAssociatedItems([])
    onHide()
  }

  const handleValidate = () => {
    if (!hasChanges) return

    setLoading(true)

    const updatedUser = {
      ...user,
      agencies: associatedAgencies.map((agencyId) => ({ id: agencyId })),
    }

    UserApi.updateUser(updatedUser)
      .then(() => {
        successToast(I18n.t('SuccessAddressUpdate'))
        setInitialAssociatedIds(associatedAgencies)
        onHide()
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      })
      .catch(() => {
        errorToast(I18n.t('ErrorAddressUpdate'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleTransferToAssociated = () => {
    if (selectedAvailable.length === 0) return

    const itemsToExpand = new Set()

    const hasPatrimonySelected = patrimonies.some((patrimony) => selectedAvailable.includes(patrimony.id))

    if (!hasPatrimonySelected) {
      patrimonies.forEach((patrimony) => {
        if (expandedAvailableItems.includes(patrimony.id)) {
          itemsToExpand.add(patrimony.id)
        }

        patrimony.sectors.forEach((sector) => {
          if (expandedAvailableItems.includes(sector.id)) {
            itemsToExpand.add(patrimony.id)
            itemsToExpand.add(sector.id)
          }
        })
      })
    }

    setExpandedAssociatedItems([...itemsToExpand])
    const newAssociatedAgencies = [...associatedAgencies, ...selectedAvailable]
    setAssociatedAgencies(newAssociatedAgencies)
    setSelectedAvailable([])
    setSelectedAssociated([])
  }

  const handleTransferToAvailable = () => {
    if (selectedAssociated.length === 0) return

    const itemsToExpand = new Set()

    patrimonies.forEach((patrimony) => {
      if (expandedAssociatedItems.includes(patrimony.id)) {
        itemsToExpand.add(patrimony.id)
      }

      patrimony.sectors.forEach((sector) => {
        if (expandedAssociatedItems.includes(sector.id)) {
          itemsToExpand.add(patrimony.id)
          itemsToExpand.add(sector.id)
        }
      })
    })

    setExpandedAvailableItems([...itemsToExpand])
    const newAssociatedAgencies = associatedAgencies.filter((id) => !selectedAssociated.includes(id))
    setAssociatedAgencies(newAssociatedAgencies)
    setSelectedAvailable([])
    setSelectedAssociated([])
  }

  const handleSearchChange = useCallback((value, type) => {
    requestAnimationFrame(() => {
      type === CONSTANTS.AGENCY_TYPES.AVAILABLE ? setSearchAvailable(value) : setSearchAssociated(value)
    })
  }, [])

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>
          {I18n.t('AssociateAddressesTo')} {userName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <WrapperLoader loading={loading}>
          <div className="row">
            <div className="col-5">
              <h4 className="mb-3">{I18n.t('AvailableAddresses')}</h4>
              <EstateTreeView
                addresses={patrimonies}
                type={CONSTANTS.AGENCY_TYPES.AVAILABLE}
                searchTerm={searchAvailable}
                selectedItems={selectedAvailable}
                onSelectionChange={setSelectedAvailable}
                onSearchChange={(value) => handleSearchChange(value, CONSTANTS.AGENCY_TYPES.AVAILABLE)}
                user={user}
                associatedAgencies={associatedAgencies}
                expandedItems={expandedAvailableItems}
                setExpandedItems={setExpandedAvailableItems}
              />
            </div>

            <div className="col-2 d-flex flex-column justify-content-center">
              <TransferButtons
                onTransferRight={handleTransferToAssociated}
                onTransferLeft={handleTransferToAvailable}
                rightDisabled={!selectedAvailable.length}
                leftDisabled={!selectedAssociated.length}
              />
            </div>

            <div className="col-5">
              <h4 className="mb-3">{I18n.t('AssociatedAddresses')}</h4>
              <EstateTreeView
                addresses={patrimonies}
                type={CONSTANTS.AGENCY_TYPES.ASSOCIATED}
                searchTerm={searchAssociated}
                selectedItems={selectedAssociated}
                onSelectionChange={setSelectedAssociated}
                onSearchChange={(value) => handleSearchChange(value, CONSTANTS.AGENCY_TYPES.ASSOCIATED)}
                user={user}
                associatedAgencies={associatedAgencies}
                expandedItems={expandedAssociatedItems}
                setExpandedItems={setExpandedAssociatedItems}
              />
            </div>
          </div>
        </WrapperLoader>
      </Modal.Body>

      <Modal.Footer className="modal-footer-centered">
        <Button variant="secondary" onClick={handleClose} className="btn secondary small">
          {I18n.t('Cancel')}
        </Button>
        <Button variant="primary" onClick={handleValidate} className="btn primary small">
          {I18n.t('Validate')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalEditAddress
